import { PropsWithChildren, ReactNode } from "react"
import { makeStyles } from "tss-react/mui"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import flattenChildren from "react-keyed-flatten-children"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { DeviceStatus, DeviceType } from "../deviceTypes"
import { Placement, StandardChip } from "../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: "2.25rem",
        padding: "0 4rem 4rem 4rem",
    },
    box: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: "1rem",
    },
    sectionHeaderWrap: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    sectionHeader: {
        fontWeight: "700",
        color: theme.palette.tailwind.slate[700],
    },
    sectionBox: {
        background: theme.palette.tailwind.slate[50],
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: "0px 2px 6px 0px #00000014",
        borderRadius: "12px",
        padding: theme.spacing(5),
        height: "100%",
    },
    infoItemRow: {
        borderBottom: 0,
        padding: 0,
    },
    itemEnd: {
        height: theme.spacing(1),
    },
    resetTopPadding: {
        paddingTop: 0,
    },
    tableHead: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        th: {
            fontWeight: "600",
        },
    },
    tableRow: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.tailwind.slate[50],
    },
    tableCell: { padding: "12px 24px" },
    label: {
        fontWeight: 400,
        color: theme.palette.tailwind.slate[600],
    },
    info: {
        color: theme.palette.tailwind.slate[900],
    },

    // Accordion
    accordion: {
        margin: 0,
        "&.Mui-expanded": {
            margin: "0",
        },
    },
    accordionSummary: {
        background: theme.palette.tailwind.slate[50],
        boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
        minHeight: "3rem",
        "& .MuiAccordionSummary-content": {
            // margin: 0,
        },
        "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
        },
        "&.Mui-expanded": {
            minHeight: "3rem",
        },
    },
    accordionTitle: {
        fontWeight: "600",
    },
    accordionDetails: {
        padding: theme.spacing(1),
        background: theme.palette.tailwind.slate[50],
    },
    skeletonWrap: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
    },
}))

export function formatDeviceDetailsText(text: string) {
    let formattedText = text.replace(/_/g, " ").toLowerCase()
    return formattedText.charAt(0).toUpperCase() + formattedText.slice(1)
}

type DeviceStatusChipProps = Readonly<{
    status: string
    placement?: Placement
}>

export function DeviceStatusChip({ status, placement }: DeviceStatusChipProps) {
    switch (status) {
        case DeviceStatus.ONLINE:
            return (
                <LocalizedStrict id="devices-table-status-column-online">
                    <StandardChip variant="success" placement={placement}>Online</StandardChip>
                </LocalizedStrict>
            )
        case DeviceStatus.OFFLINE:
            return (
                <LocalizedStrict id="devices-table-status-column-offline">
                    <StandardChip variant="red" placement={placement}>Offline</StandardChip>
                </LocalizedStrict>
            )
        case DeviceStatus.ACTIVE:
            return (
                <LocalizedStrict id="devices-table-status-column-online">
                    <StandardChip variant="success" placement={placement}>Online</StandardChip>
                </LocalizedStrict>
            )
        case DeviceStatus.INACTIVE:
            return (
                <LocalizedStrict id="devices-table-status-column-offline">
                    <StandardChip variant="red" placement={placement}>Offline</StandardChip>
                </LocalizedStrict>
            )
        default:
            return <StandardChip variant="info" placement={placement}>{formatDeviceDetailsText(status)}</StandardChip>
    }
}

type DeviceDetailsOnlyChildrenProps = Readonly<
    PropsWithChildren<{
        updateClasses?: string
    }>
>

export function DeviceDetailsContainer({ children }: DeviceDetailsOnlyChildrenProps) {
    const { classes } = useStyles()

    return <Grid className={classes.container}>{children}</Grid>
}

export function DeviceDetailsBox({ children }: DeviceDetailsOnlyChildrenProps) {
    const { classes } = useStyles()

    return <Grid className={classes.box}>{children}</Grid>
}

type DeviceDetailsSectionProps = Readonly<
    PropsWithChildren<{
        heading: React.ReactNode
        rightElement?: React.ReactNode
    }>
>

export function DeviceDetailsSection({ heading, rightElement, children }: DeviceDetailsSectionProps) {
    const { classes, cx } = useStyles()

    return (
        <Grid item>
            <Table>
                <TableBody>
                    <DeviceDetailsInfoItem
                        label={
                            <Typography variant="h5" className={classes.sectionHeader}>
                                {heading}
                            </Typography>
                        }
                    >
                        {rightElement}
                    </DeviceDetailsInfoItem>

                    {children}
                </TableBody>
            </Table>
        </Grid>
    )
}

export function DeviceDetailsSectionBoxTable({ children }: DeviceDetailsOnlyChildrenProps) {
    const { classes } = useStyles()

    return (
        <Grid className={classes.sectionBox}>
            <Table>
                <TableBody>{children}</TableBody>
            </Table>
        </Grid>
    )
}

type DeviceDetailsSectionTableProps = Readonly<
    PropsWithChildren<{
        headElements?: ReactNode
    }>
>

export function DeviceDetailsSectionTable({ children, headElements }: DeviceDetailsSectionTableProps) {
    const { classes } = useStyles()

    return (
        <Grid>
            <Table>
                {headElements && <DeviceDetailsSectionTableHead>{headElements}</DeviceDetailsSectionTableHead>}
                <TableBody>{children}</TableBody>
            </Table>
        </Grid>
    )
}
export function DeviceDetailsSectionTableHead({ children }: DeviceDetailsOnlyChildrenProps) {
    const { classes } = useStyles()
    return (
        <TableHead className={classes.tableHead}>
            <DeviceDetailsItemRow>{children}</DeviceDetailsItemRow>
        </TableHead>
    )
}

export function DeviceDetailsSectionBox({ children, updateClasses }: DeviceDetailsOnlyChildrenProps) {
    const { classes, cx } = useStyles()

    return <Grid className={cx(classes.sectionBox, updateClasses)}>{children}</Grid>
}

type DeviceDetailsHeaderBoxProps = Readonly<
    PropsWithChildren<{
        title: string
    }>
>
export function DeviceDetailsHeaderBox({ title, children }: DeviceDetailsHeaderBoxProps) {
    const { classes } = useStyles()

    return (
        <Grid className={classes.sectionHeaderWrap}>
            <Typography variant="h5" className={classes.sectionHeader}>
                {title}
            </Typography>
            {children}
        </Grid>
    )
}

type DeviceDetailsInfoItemProps = Readonly<
    PropsWithChildren<{
        label: React.ReactNode
        description?: React.ReactNode
        hide?: boolean
        hideLastRow?: boolean
    }>
>

export function DeviceDetailsInfoItem({
    label,
    description,
    hide = false,
    children,
    hideLastRow = false,
}: DeviceDetailsInfoItemProps) {
    if (hide) return null

    const { classes, cx } = useStyles()
    const [first, ...rest] = flattenChildren(children)

    return (
        <>
            <TableRow>
                <TableCell align="left" colSpan={2} className={cx(classes.infoItemRow)}>
                    <Typography variant="subtitle1" className={classes.label}>
                        {label}
                    </Typography>
                    {description && (
                        <span>
                            <br />
                            {description}
                        </span>
                    )}
                </TableCell>
                <TableCell align="right" className={cx(classes.infoItemRow)}>
                    <Typography variant="subtitle1" className={classes.info}>
                        {first}
                    </Typography>
                </TableCell>
            </TableRow>
            {rest.map((child, index) => (
                <TableRow key={index}>
                    <TableCell align="left" colSpan={2} className={cx(classes.infoItemRow)}>
                        {" "}
                    </TableCell>
                    <TableCell align="right" className={cx(classes.infoItemRow)}>
                        {child}
                    </TableCell>
                </TableRow>
            ))}
            {!hideLastRow && (
                <TableRow>
                    <TableCell colSpan={3} className={cx(classes.infoItemRow, classes.itemEnd)}>
                        {" "}
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

type DeviceDetailsInfoFullItemRowProps = Readonly<PropsWithChildren<{}>>
export function DeviceDetailsInfoFullItemRow({ children }: DeviceDetailsInfoFullItemRowProps) {
    const { classes, cx } = useStyles()

    return (
        <>
            <TableRow>
                <TableCell colSpan={3} className={classes.infoItemRow}>
                    {children}
                </TableCell>
            </TableRow>
        </>
    )
}

type DeviceDetailsItemRowProps = Readonly<
    PropsWithChildren<{
        description?: React.ReactNode
        hide?: boolean
        hideLastRow?: boolean
    }>
>

export function DeviceDetailsItemRow({ hide = false, children }: DeviceDetailsItemRowProps) {
    if (hide) return null

    const { classes, cx } = useStyles()
    const [...rest] = flattenChildren(children)

    return (
        <>
            <TableRow className={classes.tableRow}>
                {rest.map((child, index) => (
                    <TableCell align="left" key={index} className={cx(classes.infoItemRow, classes.tableCell)}>
                        {child}
                    </TableCell>
                ))}
            </TableRow>
        </>
    )
}

type DeviceDetailsAccordionProps = Readonly<
    PropsWithChildren<{
        title: string
        expanded: boolean
        setExpanded: (prev: boolean) => void
        updateClasses?: string
    }>
>
export function DeviceDetailsAccordion({ title, children, expanded, setExpanded }: DeviceDetailsAccordionProps) {
    const { classes, cx } = useStyles()
    return (
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} className={classes.accordion}>
            <DeviceDetailsAccordionSummary title={title} />
            <AccordionDetails className={classes.accordionDetails}>{children}</AccordionDetails>
        </Accordion>
    )
}

type DeviceDetailsAccordionSummaryProps = Readonly<{ title: string }>
export function DeviceDetailsAccordionSummary({ title }: DeviceDetailsAccordionSummaryProps) {
    const { classes, cx } = useStyles()
    return (
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${title}-content`}
            id={`${title}-header`}
            className={classes.accordionSummary}
        >
            <Typography className={classes.accordionTitle}>{title}</Typography>
        </AccordionSummary>
    )
}

export function DeviceDetailsSectionTableSkeleton() {
    return (
        <Grid className={""}>
            <Table>
                <DeviceDetailsSectionTableHead>
                    <Skeleton variant="rectangular" animation="wave" />
                </DeviceDetailsSectionTableHead>
                <TableBody>
                    <DeviceDetailsItemRow>
                        <Skeleton variant="rectangular" animation="wave" />
                    </DeviceDetailsItemRow>
                    <DeviceDetailsItemRow>
                        <Skeleton variant="rectangular" animation="wave" />
                    </DeviceDetailsItemRow>
                </TableBody>
            </Table>
        </Grid>
    )
}

type RepeatComponentProps = Readonly<
    PropsWithChildren<{
        count: number
    }>
>
export const RepeatComponent = ({ count, children }: RepeatComponentProps) => {
    return (
        <>
            {[...Array(count).keys()].map((index) => (
                <div key={index}>{children}</div>
            ))}
        </>
    )
}

export function DeviceDetailsSkeleton() {
    const { classes, cx } = useStyles()
    return (
        <>
            <div>
                <div className={classes.skeletonWrap}>
                    <Skeleton width="10%" height={30} />
                    <Skeleton width="10%" height={30} />
                </div>
                <RepeatComponent count={8}>
                    <div className={classes.skeletonWrap}>
                        <Skeleton width="22%" height={20} />
                        <Skeleton width="25%" height={20} />
                    </div>
                </RepeatComponent>
            </div>
            <div>
                <div className={classes.skeletonWrap}>
                    <Skeleton width="20%" height={30} />
                    <Skeleton width="10%" height={30} />
                </div>
                <DeviceDetailsSectionBox>
                    <DeviceDetailsSectionTable
                        headElements={
                            <>
                                <Skeleton width="15%" height={20} />
                                <Skeleton width="20%" height={20} />
                            </>
                        }
                    >
                        <DeviceDetailsItemRow>
                            <Skeleton width="40%" height={20} />
                            <Skeleton width="26%" height={20} />
                        </DeviceDetailsItemRow>
                        <DeviceDetailsItemRow>
                            <Skeleton width="40%" height={20} />
                            <Skeleton width="26%" height={20} />
                        </DeviceDetailsItemRow>
                    </DeviceDetailsSectionTable>
                </DeviceDetailsSectionBox>
            </div>
            <div>
                <div className={classes.skeletonWrap}>
                    <Skeleton width="20%" height={30} />
                </div>
                <DeviceDetailsSectionBox>
                    <DeviceDetailsSectionTable
                        headElements={
                            <>
                                <Skeleton width="20%" height={20} />
                                <Skeleton width="20%" height={20} />
                                <Skeleton width="20%" height={20} />
                            </>
                        }
                    >
                        <DeviceDetailsItemRow>
                            <Skeleton width="40%" height={20} />
                            <Skeleton width="26%" height={20} />
                            <Skeleton width="26%" height={20} />
                        </DeviceDetailsItemRow>
                        <DeviceDetailsItemRow>
                            <Skeleton width="40%" height={20} />
                            <Skeleton width="26%" height={20} />
                            <Skeleton width="26%" height={20} />
                        </DeviceDetailsItemRow>
                        <DeviceDetailsItemRow>
                            <Skeleton width="20%" height={20} />
                            <Skeleton width="26%" height={20} />
                            <Skeleton width="26%" height={20} />
                        </DeviceDetailsItemRow>
                    </DeviceDetailsSectionTable>
                </DeviceDetailsSectionBox>
            </div>

            <Skeleton variant="rectangular" animation="wave" />
        </>
    )
}
